/**
 * Typed wrapper for the citizen self-service pickup-location redirect on
 * service-mail. Sibling of F-076 — changes the destination from "home
 * delivery" to "pickup at a NIPOST outlet".
 *
 * Endpoint:
 *   POST /v1/mail-items/:trackingNumber/pickup-redirect
 *
 * RBAC: requires Mail/redirectPickup-own (seeded to the `customer` role by
 * 20260603_citizen_reschedule_pickup_perms.sql).
 */
import {
  requestJson,
  type RequestJsonOptions,
} from '../api-client';
import { getCustomerToken } from '../auth/tokenStore';
import { env } from '../env';

export interface RequestPickupRedirectBody {
  readonly outletId: string;
}

export interface PickupRedirectResponse {
  readonly id: string;
  readonly mailItemId: string;
  readonly customerId: string;
  readonly outletId: string;
  readonly outletCode: string;
  readonly outletName: string;
  readonly feeMinor: number;
  readonly feeNgn: string;
  readonly status: 'REQUESTED' | 'APPLIED' | string;
  readonly createdAt: string;
  readonly updatedAt: string;
}

type CallOpts = Pick<RequestJsonOptions, 'fetchImpl' | 'signal'> & {
  readonly bearerToken?: string;
};

function bearerOf(opts: CallOpts): string | undefined {
  return opts.bearerToken ?? getCustomerToken() ?? undefined;
}

export async function requestPickupRedirect(
  trackingNumber: string,
  outletId: string,
  options: CallOpts = {},
): Promise<PickupRedirectResponse> {
  const { bearerToken: _ignore, ...rest } = options;
  return requestJson<PickupRedirectResponse>({
    method: 'POST',
    baseUrl: env.serviceMailBaseUrl,
    path: `/v1/mail-items/${encodeURIComponent(trackingNumber)}/pickup-redirect`,
    body: { outletId },
    bearerToken: bearerOf(options),
    ...rest,
  });
}
